import styles from './ccf-interaction-search-cases-tab.module.scss';
import { useEffect, useState, useRef } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Stack from '@mui/material/Stack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Chip from '@mui/material/Chip';
import { emphasize, styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import makeStyles from '@mui/styles/makeStyles';

import {
  FormControl,
  MenuItem,
  Select,
  Theme,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CcfAlert,
  CcfBox,
  CcfButton,
  CcfSnackBar,
  CcfTextField,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useMsal } from '@azure/msal-react';

/* eslint-disable-next-line */
export interface CcfInteractionSearchCasesCheckboxesProps {
  selectedRowData: any;
  setRowData: any;
  rowData: any;
  setSelectedRowData: any;
  setSelectedIds: any;
  selectedIds: any;
}

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === 'light'
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    // color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
}) as typeof Chip; // TypeScript only: need a type cast here because https://github.com/Microsoft/TypeScript/issues/26591

const useStyles = makeStyles({
  suggestionField: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: '48px',
    background: '#F7F7F7',
    whiteSpace: 'normal',
  },
  suggestionInputPropsStyle: {
    '&.MuiInputBase-root': {
      color: '#3F5C69',
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '125%',
    },
  },
});

/**
 * CcfInteractionSearch - used to display Notes in contact card
 * @param props -? - CcfInteractionSearchCasesCheckboxesProps
 * @example - <CcfInteractionSearchCasesCheckboxes />
 */
export function CcfInteractionSearchCasesCheckboxes({
  selectedRowData,
  setSelectedRowData,
  setSelectedIds,
  setRowData,
  rowData,
  selectedIds,
}: CcfInteractionSearchCasesCheckboxesProps) {
  const { accounts } = useMsal();
  const classes = useStyles();
  const [translate] = useTranslator();
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');

  const assignName = `${
    loginCredentialsArray[0].charAt(0).toUpperCase() +
    loginCredentialsArray[0].slice(1).toLowerCase()
  } ${
    loginCredentialsArray[1].charAt(0).toUpperCase() +
    loginCredentialsArray[1].slice(1).toLowerCase()
  }`;

  const breadcrumbData = [
    {
      options: [
        assignName,
        'Janus Doesne',
        'John Smith',
        'Kalby Awadkansee',
        'John Smithson',
        'Janwdye Shealty',
        'Janet Dolgera',
      ],
      selectedOption: assignName,
      selectedType: 'assign',
      isSelected: false,
      id: 0,
    },
    {
      options: ['new', 'closed'],
      selectedOption: 'New',
      selectedType: 'editStatus',
      isSelected: false,
      id: 1,
    },
    {
      selectedText: '',
      selectedType: 'reply',
      isSelected: false,
      id: 2,
    },
    {
      formatoptions: ['CSV'],
      selectedOption: '',
      selectedType: 'export',
      isSelected: false,
      id: 3,
    },
  ];
  const [selectedComponent, setSelectedComponent]: any =
    useState(breadcrumbData);

  const [textAreaValue, setTextAreaValue] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [flag, setFlag] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const theme: Theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });

  useEffect(() => {
    if (flag) {
      setTimeout(() => {
        setSelectedRowData([]);
      }, 2500);
    }
  }, [flag]);

  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  function handleClick(event: any) {
    event.preventDefault();
    const setData = breadcrumbData.map((x: any, i: any) =>
      translate(x.selectedType) === event.target.textContent
        ? { ...x, isSelected: !selectedComponent[i].isSelected }
        : { ...x }
    );
    setSelectedComponent(setData);
  }

  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  const AssignComponent = () => {
    return (
      <CcfBox
        style={{
          margin: '0px 17px 13px',
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
        }}
        id={`agent_interactionSearch_assignTo_mainContainer`}
      >
        <CcfTypography
          className={styles.selectLabel}
          translationKey={'assignTo'}
        />
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '5px',
            gap: '10px',
          }}
        >
          <FormControl
            variant="outlined"
            size={'small'}
            style={{
              width: !isSmView ? '274px' : '210px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              id={`agent_interactionSearch_assignTo_dropdown`}
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[0].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, idx: any) =>
                    idx === 0
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
            >
              {breadcrumbData[0]?.options?.map(
                (option: string, index: number) => {
                  return (
                    <MenuItem
                      value={option}
                      key={index}
                      className={styles.labelText}
                      id={`agent_interactionSearch_AssignTo_filter_${option}`}
                    >
                      {option}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>
          <CcfButton
            className={styles.btn}
            primary
            id={`agent_interactionSearch_assign_button`}
            onClick={() => {
              const updatedData = JSON.parse(JSON.stringify(rowData));
              selectedRowData.forEach((updateObj: any) => {
                const objIndex = updatedData.findIndex(
                  (obj: any) => obj.id === updateObj.id
                );
                if (objIndex !== -1) {
                  updatedData[objIndex] = {
                    ...updatedData[objIndex],
                    ...updateObj,
                    inboxAssigneeUser: selectedComponent[0].selectedOption,
                    ownerAssigneeUser: selectedComponent[0].selectedOption,
                  };
                }
              });
              setRowData(updatedData);
              setToastMessage(
                `${translate('statusChangeToastMsg')} ${
                  selectedComponent[0].selectedOption
                }`
              );
              setShowToast(true);
              setFlag(true);

              setSelectedIds([]);
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, idx: any) =>
                  idx === 0 ? { ...item, selectedOption: '' } : item
                )
              );
              // setSelectedRowData([]);
            }}
          >
            <CcfTypography
              className={styles.labelText}
              translationKey={'assign'}
            />
          </CcfButton>{' '}
        </CcfBox>
      </CcfBox>
    );
  };

  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  const EditStatusComponent = () => {
    return (
      <CcfBox
        style={{
          margin: '0px 17px 13px',
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
          width: '375px',
        }}
        id={`agent_interactionSearch_edit_breadcrumb_container`}
      >
        <CcfTypography
          className={styles.selectLabel}
          translationKey={'changeStatusTo'}
        />
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '5px',
            gap: '10px',
          }}
        >
          <FormControl
            variant="outlined"
            size={'small'}
            style={{
              width: !isSmView ? '274px' : '210px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              id={`agent_interactionSearch_changeStatus_dropdown`}
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[1].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, idx: any) =>
                    idx === 1
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
            >
              {breadcrumbData[1]?.options?.map(
                (option: string, index: number) => {
                  return (
                    <MenuItem
                      value={option}
                      key={index}
                      id={`agent_interactionSearch_AssignTo_filter_${option}`}
                    >
                      {translate(option)}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>
          <CcfButton
            className={styles.btn}
            primary
            id={`agent_interactionSearch_assign_button`}
            onClick={() => {
              const updatedData = JSON.parse(JSON.stringify(rowData));
              selectedRowData.forEach((updateObj: any) => {
                const objIndex = updatedData.findIndex(
                  (obj: any) => obj.id === updateObj.id
                );
                if (objIndex !== -1) {
                  updatedData[objIndex] = {
                    ...updatedData[objIndex],
                    ...updateObj,
                    status: selectedComponent[1].selectedOption,
                  };
                }
              });
              setRowData(updatedData);
              setToastMessage(
                `${translate('selectedinteractionstatushasbeenchangeto')}  ${
                  selectedComponent[1].selectedOption
                }`
              );
              setShowToast(true);
              setFlag(true);

              setSelectedIds([]);
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, idx: any) =>
                  idx === 1 ? { ...item, selectedOption: '' } : item
                )
              );
            }}
          >
            <CcfTypography
              className={styles.btnText}
              translationKey={'change'}
            />
          </CcfButton>{' '}
        </CcfBox>
        <CcfTypography
          style={{ margin: '15px 4px 0px', width: '300px' }}
          className={styles.labelText}
        >
          {selectedRowData.length}{' '}
          {translate('selectedinteractionswillhavetheirstatus ')}
          {translate('changeStatusTo')} {selectedComponent[1].selectedOption}.
        </CcfTypography>
      </CcfBox>
    );
  };

  /**
   * handleInputChange handle item click event
   * @example <handleInputChange />
   */
  function handleInputChange(event: any) {
    // event.preventDefault();

    setTextAreaValue(event);
    setSelectedComponent((prevData: any) =>
      prevData.map((item: any, idx: any) =>
        idx === 2 ? { ...item, selectedText: event } : item
      )
    );
  }
  useEffect(() => {
    handleInputChange(textAreaRef?.current?.value);
  }, [textAreaRef]);
  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  const ReplyComponent = () => {
    return (
      <CcfBox
        className={styles.replyHeader}
        sx={{ width: !isSmView ? '440px' : '300px' }}
        id={`agent_interactionSearch_reply_breadcrumb_container`}
      >
        <CcfTypography
          className={styles.selectLabel}
          translationKey={'reply'}
        />
        <CcfBox
          className={styles.replyTextArea}
          sx={{ width: !isSmView ? '441px' : '300px !important' }}
        >
          <CcfTextField
            fullWidth
            multiline={true}
            autoFocus
            rows={3}
            maxRows={3}
            id="agent_interactionSearch_cases_reply_textarea"
            className={styles.reply_textarea}
            // value={textAreaValue}
            // onChange={(event: any) => {
            //   handleInputChange(event);
            // }}
            InputProps={{
              className: classes.suggestionInputPropsStyle,
              disableUnderline: true,
            }}
            inputRef={textAreaRef}
            variant="standard"
            size="medium"
            focused={false}
          ></CcfTextField>
          <CcfBox>
            <img
              src={'/assets/textAreaIcons.png'}
              style={{ width: '130px', height: '35px' }}
              alt={'textAreaIcons'}
            />
          </CcfBox>
        </CcfBox>
        <CcfBox
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <CcfButton
            className={styles.sendBtn}
            primary
            id={`agent_interactionSearch_reply_send_button`}
            onClick={() => {
              setToastMessage(`${translate('customizedreplymessage')}`);
              setShowToast(true);
              setFlag(true);

              setSelectedIds([]);
              setTextAreaValue('');
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, idx: any) =>
                  idx === 2 ? { ...item, selectedText: '' } : item
                )
              );
            }}
          >
            <CcfTypography className={styles.btnText} translationKey={'send'} />
          </CcfButton>{' '}
        </CcfBox>
        <CcfBox style={{ marginTop: '10px' }}>
          <CcfTypography style={{ fontSize: '12px' }}>
            {translate('interactionStatusChangeNote1')}
            <span style={{ fontWeight: '600' }}>{translate('new')} </span>
            {translate('interactionStatusChangeNote2')}
            <span style={{ fontWeight: '600' }}>
              {translate('closed')}
            </span>{' '}
            {translate('interactions')}.
          </CcfTypography>
        </CcfBox>
      </CcfBox>
    );
  };

  /**
   * arrayToCSV handle item click event
   * @example <arrayToCSV />
   */
  function arrayToCSV(data: any) {
    const csvRows = [];

    // Get the headers (keys of the objects)
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(','));

    // Loop through the rows
    for (const row of data) {
      const values = headers.map((header) => {
        const escaped = ('' + row[header]).replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  }

  /**
   * arrayToCSV handle item click event
   * @example <arrayToCSV />
   */
  function downloadCSV(data: any, filename = 'InteractionData.csv') {
    const updatedData = data.map((x: any) => ({
      ...x,
      lastUpdated: new Date(x.lastUpdated),
    }));
    const csvString = arrayToCSV(updatedData);
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    a.click();
    window.URL.revokeObjectURL(url);
  }
  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  const ExportComponent = () => {
    return (
      <CcfBox
        style={{
          margin: '0px 17px 13px',
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
          width: !isSmView ? '410px' : '300px',
        }}
        id={`agent_interactionSearch_export_breadcrumb_container`}
      >
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '5px',
            gap: '10px',
            flexDirection: 'column',
          }}
          id={`agent_interactionSearch_export_container`}
        >
          <CcfTypography
            className={styles.selectLabel}
            translationKey={'exportFormat'}
          />
          <FormControl
            id={`agent_interactionSearch_export_dropdown`}
            variant="outlined"
            // className={classes.formControl}
            size={'small'}
            style={{
              width: !isSmView ? '410px' : '300px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              placeholder="Select file type"
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[1].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, idx: any) =>
                    idx === 1
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
              // className={classes.input}
              // label={label}
            >
              {breadcrumbData[3]?.formatoptions?.map(
                (option: string, index: number) => {
                  return (
                    <MenuItem
                      value={option}
                      key={index}
                      id={`agent_interactionSearch_Export_filter_${option}`}
                    >
                      {option}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>{' '}
        </CcfBox>
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '5px',
            gap: '10px',
            flexDirection: 'column',
          }}
          id={`agent_interactionSearch_location_container`}
        >
          <CcfTypography
            className={styles.selectLabel}
            translationKey={'location'}
          />

          <FormControl
            variant="outlined"
            // className={classes.formControl}
            size={'small'}
            style={{
              width: !isSmView ? '410px' : '300px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              id={`agent_interactionSearch_location_dropdown`}
              placeholder="Select"
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[1].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, idx: any) =>
                    idx === 1
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
              // className={classes.input}
              // label={label}
            >
              {breadcrumbData[3]?.options?.map(
                (option: string, index: number) => {
                  return (
                    <MenuItem
                      value={option}
                      key={index}
                      id={`agent_interactionSearch_Export_filter_${option}`}
                    >
                      {option}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>
        </CcfBox>
        <CcfBox
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            marginTop: '10px',
          }}
        >
          <CcfButton
            className={styles.sendBtn}
            primary
            id={`agent_interactionSearch_export_button`}
            onClick={() => {
              downloadCSV(selectedRowData);
              setFlag(true);
              setSelectedIds([]);
              setToastMessage(`${translate('importToasterMsg')}`);
              setShowToast(true);
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, idx: any) =>
                  idx === 1 ? { ...item, selectedOption: '' } : item
                )
              );
            }}
          >
            <CcfTypography
              className={styles.btnText}
              translationKey={'export'}
            />
          </CcfButton>{' '}
        </CcfBox>
      </CcfBox>
    );
  };

  return (
    <Stack spacing={2} id={`agent_interactionSearch_breadcrumbs_header`}>
      <Breadcrumbs
        style={{ padding: '15px 17px' }}
        aria-label="breadcrumb"
        aria-hidden="false"
        separator=""
      >
        <StyledBreadcrumb
          component="a"
          className={
            selectedComponent[0].selectedType === 'assign' &&
            selectedComponent[0].isSelected
              ? styles.activeTab
              : undefined
          }
          id={`agent_interactionSearch_assign_breadcrumb`}
          style={{
            color: '#003D7A',
            fontWeight: '600',
            fontSize: '14px',
          }}
          label={translate('assign')}
          aria-hidden="false"
          onClick={handleClick}
          icon={
            selectedComponent[0].selectedType === 'assign' &&
            selectedComponent[0].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        />
        <StyledBreadcrumb
          component="a"
          className={
            selectedComponent[1].selectedType === 'editStatus' &&
            selectedComponent[1].isSelected
              ? styles.activeTab
              : undefined
          }
          id={`agent_interactionSearch_editStatus_breadcrumb`}
          aria-hidden="false"
          style={{ color: '#003D7A', fontWeight: '600', fontSize: '14px' }}
          label={translate('editStatus')}
          onClick={handleClick}
          icon={
            selectedComponent[1].selectedType === 'editStatus' &&
            selectedComponent[1].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        />
        <StyledBreadcrumb
          component="a"
          className={
            selectedComponent[2].selectedType === 'reply' &&
            selectedComponent[2].isSelected
              ? styles.activeTab
              : undefined
          }
          id={`agent_interactionSearch_reply_breadcrumb`}
          aria-hidden="false"
          label={translate('reply')}
          onClick={handleClick}
          style={{ color: '#003D7A', fontWeight: '600', fontSize: '14px' }}
          icon={
            selectedComponent[2].selectedType === 'reply' &&
            selectedComponent[2].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        />
        <StyledBreadcrumb
          aria-hidden="false"
          id={`agent_interactionSearch_export_breadcrumb`}
          className={
            selectedComponent[3].selectedType === 'export' &&
            selectedComponent[3].isSelected
              ? styles.activeTab
              : undefined
          }
          component="a"
          label={translate('export')}
          onClick={handleClick}
          style={{ color: '#003D7A', fontWeight: '600', fontSize: '14px' }}
          icon={
            selectedComponent[3].selectedType === 'export' &&
            selectedComponent[3].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        />
      </Breadcrumbs>
      <CcfBox>
        <CcfSnackBar
          open={showToast}
          autoHideDuration={3000}
          onClose={() => {
            setShowToast(false);
            setToastMessage('');
          }}
          anchorOrigin={{
            vertical: isSmView ? 'top' : 'bottom',
            horizontal: 'right',
          }}
        >
          <div>
            <CcfAlert
              closeAlert={() => {
                setShowToast(false);
                setToastMessage('');
              }}
              severity="success"
            >
              {toastMessage}
            </CcfAlert>
          </div>
        </CcfSnackBar>
      </CcfBox>
      {selectedComponent[0].selectedType === 'assign' &&
        selectedComponent[0].isSelected && <AssignComponent />}
      {selectedComponent[1].selectedType === 'editStatus' &&
        selectedComponent[1].isSelected && <EditStatusComponent />}
      {selectedComponent[2].selectedType === 'reply' &&
        selectedComponent[2].isSelected && <ReplyComponent />}
      {selectedComponent[3].selectedType === 'export' &&
        selectedComponent[3].isSelected && <ExportComponent />}
    </Stack>
  );
}

export default CcfInteractionSearchCasesCheckboxes;
